// utils/sessionRefresh.js
//
// Sliding session for the admin dashboard (critical for the iOS home-screen
// PWA, which gets frozen/killed and resurrected constantly): on app boot and
// every time the app returns to the foreground, silently exchange the current
// (still-valid) token for a fresh 30-day one via POST /auth/refresh.
// Net effect: as long as the user opens the app at least once a month, the
// session NEVER expires (capped server-side at 90 days absolute).
//
// Uses BARE axios on purpose: going through apiClient would run the 401
// interceptor, and a transient failure of this background refresh would
// hard-logout the user — the exact bug this module exists to fix. A genuinely
// dead token is handled by the interceptor on the next real request.
import axios from 'axios';
import { persistSession } from './tokenStore';

const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:4000';

const REFRESH_THROTTLE_MS = 24 * 60 * 60 * 1000; // at most once a day
const STAMP_KEY = 'token-refreshed-at';

let initialized = false;

/** Age of the current token in ms, from its JWT `iat` claim (null if unreadable). */
const tokenAgeMs = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(window.atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.iat) return null;
    return Date.now() - payload.iat * 1000;
  } catch {
    return null;
  }
};

const maybeRefresh = async () => {
  const age = tokenAgeMs();
  if (age === null) return; // no admin session → nothing to do (owner/guest tokens untouched)

  // Throttle: a fresh token (<24h) or a refresh done <24h ago → skip.
  const last = parseInt(localStorage.getItem(STAMP_KEY) || '0', 10);
  if (age < REFRESH_THROTTLE_MS || Date.now() - last < REFRESH_THROTTLE_MS) return;

  try {
    const res = await axios.post(`${BASE_URL}/auth/refresh`, null, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (res.data?.token) {
      persistSession(res.data.token); // localStorage + cookie mirror
      localStorage.setItem(STAMP_KEY, String(Date.now()));
    }
  } catch {
    // Silent — a dead token will be dealt with by the 401 interceptor on the
    // next real request; a transient failure just retries on the next resume.
  }
};

/** Idempotent init: refresh now, then on every return to foreground. */
export const initSessionRefresh = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  maybeRefresh();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') maybeRefresh();
  });
};

export default initSessionRefresh;
